// Owner-gated aura skill lines, derived from the current held owner state.
// Replace only changed skills; raw anchor occupancy, other clusters and unchanged
// entries retain their identities. The caller supplies eligibility from the shared
// creature-status reader and detects hidden source lines before publishing a delta.
import { phpJsonInt } from './php-json-values';
import type { ItemFieldRow } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { AURA_OWNER_STATE_FREE_LAYERS, readCreaturePayloadStatuses, readCreatureStatuses,
    type AuraOwnerStateResolver } from './creature-status-reader';
import { isSelfStatusOrigin } from '../statuses/status-origin';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';
const canon = (v: unknown): string => JSON.stringify(v);

export interface AuraOwnerGatedRow {
    /** the held status ROW id (the lines' href_id) */
    statusId: string;
    /** its resolved definition */
    def: Row;
    /** the gate's verdict against the owner's current state (and not a hostile self-anchor) */
    active: boolean;
}
export interface AuraOwnerSkillLinesInput {
    skills: unknown;
    /** upper-cased status row ids in statuses.target order (the status walk's order) */
    statusOrder: readonly string[];
    gated: readonly AuraOwnerGatedRow[];
    skillEntries: ReadonlyArray<{ item_id: string; variable_name: string }>;
    layerEntries: (row: Row, layer: string) => unknown;
    /** whether an entry-to-be-added might sit beside HIDDEN lines: the skill stores no breakdown of that kind and another source targets it */
    otherSourceTargets: (skill: { item_id: string; variable_name: string }, kind: 'adv' | 'mod') => boolean;
    /** layer_entry → skill match (compileInventorySensitiveSkills' layerEntryTargetsSkill) */
    targets: (entry: unknown, skillId: string, variableName: string) => boolean;
    /** skill_roll_mod entry parse (skillModEntry) */
    modEntry: (entry: unknown) => { skill: string; mod: number } | null;
}
export type AuraOwnerSkillLinesResult = { ok: true; skills: Row; changed: boolean; objectsCopied?: number } | { ok: false; reason: string };

export interface AuraOwnerSkillDeltaInput extends Omit<AuraOwnerSkillLinesInput, 'gated' | 'statusOrder'> {
    holder: ItemFieldRow;
    /** Before a status edit; retired rows remove their existing skill lines. */
    previousHolder?: ItemFieldRow;
    ownerId: string;
    /** Existing held definition/roster readers; neither callback may fetch or compile. */
    definition: (id: string) => ItemFieldRow | null;
    resolveOwner: AuraOwnerStateResolver;
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
}

/** Resource/status subscribers use this after installing an owner's changed fields.
 * Only rows emitted by that owner contribute to this delta. Eligibility is read once
 * through the shared payload reader; structural rows are never removed or replaced.
 * Callers publish changed skill paths, using the result's retained entry identities.
 */
export function rederiveAuraOwnerSkillDelta(input: AuraOwnerSkillDeltaInput): AuraOwnerSkillLinesResult {
    const rows = Object.values(readCreatureStatuses(input.holder, 'target', input.definition)).filter(isObj);
    const ownerId = upper(input.ownerId);
    const affected = rows.filter(row => upper(readLayerListEntryUuid(row.status_caster_id)) === ownerId
        && isObj(row.status_arr) && input.layerValue(row.status_arr, 'if_aura_owner_has_status_effect_active') != null);
    if (input.previousHolder) for (const row of Object.values(readCreatureStatuses(input.previousHolder, 'target', input.definition)).filter(isObj)) {
        if (upper(readLayerListEntryUuid(row.status_caster_id)) === ownerId && isObj(row.status_arr)
            && input.layerValue(row.status_arr, 'if_aura_owner_has_status_effect_active') != null
            && !affected.some(current => upper(readLayerListEntryUuid(current.status_id)) === upper(readLayerListEntryUuid(row.status_id)))) affected.push(row);
    }
    if (!affected.length) return { ok: true, skills: isObj(input.skills) ? input.skills : {}, changed: false };
    // Limit the eligibility read to these rows, preserving their original order. The
    // resolver reads the current canonical owner, including an elided zero vitality.
    const payload = Object.values(readCreaturePayloadStatuses({ statuses: { target: rows.filter(row => affected.includes(row)) } }, input.layerValue,
        input.definition, input.resolveOwner)).filter(isObj);
    const active = new Set(payload.map(row => upper(readLayerListEntryUuid(row.status_id))));
    const gated: AuraOwnerGatedRow[] = [];
    for (const row of affected) {
        const def = row.status_arr as Row, layers = isObj(def.item_layer_types) ? def.item_layer_types : {};
        const statusId = upper(readLayerListEntryUuid(row.status_id));
        const origin = upper(readLayerListEntryUuid(row.status_origin_id));
        const itemId = upper(readLayerListEntryUuid(row.status_item_id) || readLayerListEntryUuid(def));
        if (origin && origin !== itemId && !isSelfStatusOrigin(statusId, origin))
            return { ok: false, reason: 'unported:aura-owner-payload:origin' };
        const foreign = Object.keys(layers).find(name => !AURA_OWNER_STATE_FREE_LAYERS.has(name));
        if (foreign) return { ok: false, reason: `unported:aura-owner-payload:${foreign}` };
        gated.push({ statusId, def, active: active.has(statusId) });
    }
    return rederiveAuraOwnerSkillLines({ ...input, gated,
        statusOrder: rows.map(row => upper(readLayerListEntryUuid(row.status_id))) });
}

const ENTRY_ORDER = ['tier_level', 'sources', 'adv_dis_stacks', 'adv_dis_breakdown', 'raw_sp', 'forced_natural_main_roll', 'skill_rolls_mod', 'skill_roll_mod', 'skill_roll_mod_breakdown'];

export function rederiveAuraOwnerSkillLines(input: AuraOwnerSkillLinesInput): AuraOwnerSkillLinesResult {
    const prior: Row = isObj(input.skills) ? input.skills : {};
    let out = prior;
    if (!input.gated.length) return { ok: true, skills: out, changed: false };
    const gatedIds = new Set(input.gated.map(g => upper(g.statusId)));
    const index = new Map(input.statusOrder.map((id, i) => [upper(id), i] as const));
    const isStatusLine = (l: Row): boolean => String(l.kind ?? '') === 'status_effect' || index.has(upper(l.href_id));
    const listOf = (v: unknown): unknown[] => Array.isArray(v) ? v : v === null || v === undefined || v === '' ? [] : isObj(v) && !('item_id' in v) && !('items' in v) ? Object.values(v) : [v];
    const linesOf = (entry: unknown, key: string): Row[] => {
        const b = isObj(entry) ? entry[key] : undefined;
        return isObj(b) && Array.isArray(b.lines) ? (b.lines as unknown[]).filter(isObj) : [];
    };
    /** before the first STATUS line of a later status row; else at the end (status lines are last) */
    const insert = (lines: Row[], add: Row[], at: number): void => {
        if (!add.length) return;
        let pos = lines.findIndex(l => isStatusLine(l) && (index.get(upper(l.href_id)) ?? Number.POSITIVE_INFINITY) > at);
        if (pos < 0) pos = lines.length;
        lines.splice(pos, 0, ...add);
    };
    let changed = false, minted = false, objectsCopied = 0;
    for (const skill of input.skillEntries) {
        const variable = skill.variable_name;
        const entry = out[variable];
        const advBefore = linesOf(entry, 'adv_dis_breakdown'), modBefore = linesOf(entry, 'skill_roll_mod_breakdown');
        // a zero skill_roll_mod total stores NO breakdown, so a gated aura's mod line may be hidden beside a cancelling one in EITHER
        // direction (Codex review 2026-09-30: an aura +1 cancelling another status's -1 — dormant now, PHP shows the -1 again)
        const auraTargetsMod = input.gated.some(g => listOf(input.layerEntries(g.def, 'skill_roll_mod')).some(raw => {
            const parsed = input.modEntry(raw); return !!parsed && parsed.mod !== 0 && parsed.skill === upper(skill.item_id);
        }));
        if (auraTargetsMod && !(isObj(entry) && isObj(entry.skill_roll_mod_breakdown)) && input.otherSourceTargets(skill, 'mod')) return { ok: false, reason: `unported:aura-owner:hidden-mod-lines:${variable}` };
        const adv = advBefore.filter(l => !gatedIds.has(upper(l.href_id)));
        const mod = modBefore.filter(l => !gatedIds.has(upper(l.href_id)));
        let addedAdv = false;
        for (const g of input.gated) {
            if (!g.active) continue;
            const at = index.get(upper(g.statusId)) ?? Number.POSITIVE_INFINITY;
            const advAdd: Row[] = [];
            for (const [layer, value] of [['skill_roll_adv', 1], ['skill_roll_dis', -1]] as const) {
                for (const raw of listOf(input.layerEntries(g.def, layer))) if (input.targets(raw, skill.item_id, variable)) advAdd.push({ value, kind: 'status_effect', href_id: upper(g.statusId) });
            }
            const modAdd: Row[] = [];
            for (const raw of listOf(input.layerEntries(g.def, 'skill_roll_mod'))) {
                const parsed = input.modEntry(raw);
                if (parsed && parsed.mod !== 0 && parsed.skill === upper(skill.item_id)) modAdd.push({ value: parsed.mod, kind: 'status_effect', href_id: upper(g.statusId) });
            }
            insert(adv, advAdd, at); insert(mod, modAdd, at);
            addedAdv ||= advAdd.length > 0;
        }
        if (canon(adv) === canon(advBefore) && canon(mod) === canon(modBefore)) continue;
        // Older held entries without a breakdown may hide cancelling non-status lines; native entries retain them.
        if (addedAdv && !(isObj(entry) && isObj(entry.adv_dis_breakdown)) && input.otherSourceTargets(skill, 'adv')) return { ok: false, reason: `unported:aura-owner:hidden-lines:${variable}` };
        const base: Row = isObj(entry) ? { ...entry } : { tier_level: 0, sources: ['default_flawed'] };
        if (isObj(entry)) objectsCopied++;
        if (!isObj(entry)) minted = true;
        delete base.adv_dis_stacks; delete base.adv_dis_breakdown; delete base.skill_roll_mod; delete base.skill_roll_mod_breakdown;
        const stacks = adv.reduce((sum, l) => sum + phpJsonInt(l.value ?? 0), 0);
        if (stacks !== 0) { base.adv_dis_stacks = stacks; base.adv_dis_breakdown = { lines: adv }; }
        else if (adv.some(isStatusLine) || (isObj(entry) && isObj(entry.adv_dis_breakdown) && adv.length > 0)) base.adv_dis_breakdown = { lines: adv };
        const total = mod.reduce((sum, l) => sum + phpJsonInt(l.value ?? 0), 0);
        if (total !== 0) { base.skill_roll_mod = total; base.skill_roll_mod_breakdown = { lines: mod }; }
        const next: Row = {};
        for (const k of ENTRY_ORDER) if (k in base) next[k] = base[k];
        for (const [k, v] of Object.entries(base)) if (!(k in next)) next[k] = v;
        const pure = Object.keys(next).length === 2 && phpJsonInt(next.tier_level ?? 0) === 0 && canon(next.sources) === canon(['default_flawed']);
        if (out === prior) { out = { ...prior }; objectsCopied++; }
        if (pure) delete out[variable]; else out[variable] = next;
        changed = true;
    }
    if (!minted) return { ok: true, skills: out, changed, objectsCopied };
    const ordered: Row = {};
    for (const e of input.skillEntries) if (out[e.variable_name] !== undefined) ordered[e.variable_name] = out[e.variable_name];
    for (const [k, v] of Object.entries(out)) if (!(k in ordered)) ordered[k] = v;
    return { ok: true, skills: ordered, changed, objectsCopied };
}
